import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import logo from "@/assets/logo.webp";
import { trackPhoneClick } from "@/lib/tracking";
import { SITE, SERVICES, servicePath } from "@/lib/site";
import { prefetchRoute } from "@/lib/routePrefetch";

const Icon = ({ path, className = "h-4 w-4" }: { path: string; className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d={path} />
  </svg>
);

const PHONE_PATH = "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.62 2.6a2 2 0 0 1-.45 2.11L8 9.72a16 16 0 0 0 6.28 6.28l1.29-1.28a2 2 0 0 1 2.11-.45c.83.29 1.7.5 2.6.62A2 2 0 0 1 22 16.92Z";
const MENU_PATH = "M4 12h16M4 6h16M4 18h16";
const CLOSE_PATH = "M18 6 6 18M6 6l12 12";
const CHEVRON_DOWN = "m6 9 6 6 6-6";

const aboutLinks = [
  { to: "/about/", label: "About Us" },
  { to: "/about/frequently-asked-questions/", label: "Frequently Asked Questions" },
  { to: "/about/service-areas/", label: "Service Areas" },
  { to: "/about/reviews/", label: "Reviews" },
  { to: "/blog/", label: "Blog" },
  { to: "/about/career/", label: "Career" },
];

const serviceLinks = SERVICES.map((s) => ({ to: servicePath(s.slug), label: s.name }));

const constructionLinks = [
  { to: "/custom-home-builds/project-lanyon-ln/", label: "Project Lanyon Ln." },
  { to: "/custom-home-builds/project-driftwood-drive/", label: "Project Driftwood Drive" },
];

const Dropdown = ({ label, parentTo, items }: { label: string; parentTo: string; items: { to: string; label: string }[] }) => (
  <div className="group relative">
    <Link
      to={parentTo}
      onMouseEnter={() => prefetchRoute(parentTo)}
      onFocus={() => prefetchRoute(parentTo)}
      className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground hover:text-primary"
    >
      {label} <Icon path={CHEVRON_DOWN} className="h-3.5 w-3.5" />
    </Link>
    <div className="invisible absolute left-0 top-full z-50 w-64 -translate-y-1 rounded-lg border border-border bg-card p-2 opacity-0 shadow-lg transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          onMouseEnter={() => prefetchRoute(item.to)}
          onFocus={() => prefetchRoute(item.to)}
          className="block rounded-md px-3 py-2 text-sm text-foreground hover:bg-secondary hover:text-primary"
        >
          {item.label}
        </Link>
      ))}
    </div>
  </div>
);

const navItem = "px-3 py-2 text-sm font-semibold text-foreground hover:text-primary";

const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<string | null>(null);

  return (
    <header className="fixed left-0 right-0 top-0 z-50 w-full border-b border-border bg-background lg:sticky">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="MD Custom Construction logo" width={360} height={201} loading="eager" decoding="async" className="h-10 w-auto md:h-12" />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          <Dropdown label="Home Remodeling Services" parentTo="/home-remodeling-services/" items={serviceLinks} />
          <Dropdown label="New Home Construction" parentTo="/custom-home-builds/" items={constructionLinks} />
          <Dropdown label="About" parentTo="/about/" items={aboutLinks} />
          <NavLink to="/md-custom-construction-showroom/" onMouseEnter={() => prefetchRoute("/md-custom-construction-showroom/")} onFocus={() => prefetchRoute("/md-custom-construction-showroom/")} className={navItem}>Showroom</NavLink>
          <NavLink to="/contact/" onMouseEnter={() => prefetchRoute("/contact/")} onFocus={() => prefetchRoute("/contact/")} className={navItem}>Contact</NavLink>
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <a href={SITE.phoneHref} onClick={trackPhoneClick} className="phone-button">
            <Icon path={PHONE_PATH} /> {SITE.phone}
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <a href={SITE.phoneHref} onClick={trackPhoneClick} className="phone-button text-xs">
            <Icon path={PHONE_PATH} /> Call
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            className="rounded-md border border-border p-2 text-foreground"
            aria-label="Toggle menu"
          >
            <Icon path={open ? CLOSE_PATH : MENU_PATH} className="h-5 w-5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background lg:hidden">
          <div className="mx-auto max-w-7xl space-y-1 px-4 py-4">
            {[
              { key: "services", label: "Home Remodeling Services", parent: "/home-remodeling-services/", items: serviceLinks },
              { key: "construction", label: "New Home Construction", parent: "/custom-home-builds/", items: constructionLinks },
              { key: "about", label: "About", parent: "/about/", items: aboutLinks },
            ].map((group) => (
              <div key={group.key}>
                <button
                  onClick={() => {
                    prefetchRoute(group.parent);
                    setMobileSection(mobileSection === group.key ? null : group.key);
                  }}
                  className="flex w-full items-center justify-between rounded-md px-3 py-2 font-semibold text-foreground hover:bg-secondary"
                >
                  {group.label}
                  <Icon path={CHEVRON_DOWN} className={`h-4 w-4 transition-transform ${mobileSection === group.key ? "rotate-180" : ""}`} />
                </button>
                {mobileSection === group.key && (
                  <div className="ml-3 border-l border-border pl-3">
                    <Link to={group.parent} onClick={() => setOpen(false)} onTouchStart={() => prefetchRoute(group.parent)} className="block rounded-md px-3 py-1.5 text-sm font-medium text-primary">
                      Overview
                    </Link>
                    {group.items.map((item) => (
                      <Link key={item.to} to={item.to} onClick={() => setOpen(false)} onTouchStart={() => prefetchRoute(item.to)} className="block rounded-md px-3 py-1.5 text-sm text-foreground hover:bg-secondary">
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <Link to="/md-custom-construction-showroom/" onClick={() => setOpen(false)} onTouchStart={() => prefetchRoute("/md-custom-construction-showroom/")} className="block rounded-md px-3 py-2 font-semibold text-foreground hover:bg-secondary">Showroom</Link>
            <Link to="/contact/" onClick={() => setOpen(false)} onTouchStart={() => prefetchRoute("/contact/")} className="block rounded-md px-3 py-2 font-semibold text-foreground hover:bg-secondary">Contact</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;